import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import { useAppSelector } from "../../redux";
import { Commet } from "react-loading-indicators";

const margin = { top: 100, right: 20, bottom: 30, left: 30 };

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

const PieChart = ({ width, height }) => {
  const mergeDatasets = (...datasets) => {
    const mergedData = {};

    datasets.forEach((data, index) => {
      data.forEach((item) => {
        const meterType = index === 0 ? "Grid electric" : "campus solar";
        const scaleFactor = meterType === "solar" ? 1 : 1;

        if (!mergedData[meterType]) {
          mergedData[meterType] = {
            meterType: meterType,
            curValue: 0,
            count: 0,
          };
        }
        mergedData[meterType].curValue += Math.abs(item.curValue) * scaleFactor;
        mergedData[meterType].count++;
      });
    });

    // Scale curValue by count
    Object.values(mergedData).forEach((item) => {
      item.curValue /= item.count;
    });

    return Object.values(mergedData);
  };

  // puts data into an easier to use format --> potentially move to backend but doesn't seem to work
  const graphData = mergeDatasets(
    useAppSelector((state) => state.electric.data),
    useAppSelector((state) => state.solar.data)
  );

  const ref = useRef();

  useEffect(() => {
    if (graphData.length === 0) {
      const retryTimeout = setTimeout(() => {
        // Call the useEffect again after 10 seconds
        clearTimeout(retryTimeout);
      }, 10000); // 10 seconds

      return; // Exit early without rendering the chart
    }

    // Clear old data
    d3.select(ref.current).selectAll("*").remove();

    // set the dimensions and margins of the graph

    const radius = Math.min(width, height) / 2;
    // Compute total count of meters
    const totalCount = graphData.reduce((sum, d) => sum + d.count, 0);

    // Create the color scale.
    const color = d3
      .scaleOrdinal()
      .domain(graphData.map((d) => d.meterType))
      .range(["#B6D67F", "#F2A43D"]);

    // Create the pie layout and arc generator.
    const pie = d3
      .pie()
      .sort(null)
      .value((d) => d.curValue)
      .padAngle(0.03);
    const outerRadiusScale = d3
      .scaleLinear()
      .domain([0, totalCount])
      .range([0, Math.min(width, height) - margin.top * 2 - 50]);

    const arcs = pie(graphData);
    arcs.forEach((d) => {
      d.outerRadius = outerRadiusScale(d.data.count);
    });

    const arc = d3
      .arc()
      .innerRadius(120)
      .outerRadius(Math.min(width, height - margin.top) / 2 - margin.top)
      .cornerRadius(5);

    const labelRadius = arc.outerRadius()() * 0.95;

    // A separate arc generator for labels.
    const arcLabel = d3.arc().innerRadius(labelRadius).outerRadius(labelRadius);

    // Create the SVG container.
    const svg = d3
      .select(ref.current)
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", [-width / 2, -height / 2, width, height])
      // .attr("class", "font-national")
      .attr("style", "min-width: 100%; height: auto; font: 30px National2;");

    // Add a sector path for each value.
    svg
      .append("g")
      .attr("stroke", "white")
      .selectAll()
      .data(arcs)
      .join("path")
      .attr("fill", (d) => {
        const fillColor = color(d.data.meterType);
        console.log("Color for", d.data.meterType, ":", fillColor);
        return fillColor;
      })
      .attr("d", arc)
      .append("title")
      .text((d) => `${d.data.meterType}: ${String(d.data.curValue)}`);

    // Append lines and labels for each arc.
    svg
      .append("g")
      .attr("text-anchor", "middle")
      .style("fill", "darkgreen") // Set the font color here
      .selectAll()
      .data(arcs)
      .join("g")
      .attr("transform", (d) => `translate(${arc.centroid(d)})`);

    const outerArc = d3
      .arc()
      .innerRadius(radius * 0.75)
      .outerRadius(radius * 0.8);

    const innerArc = d3
      .arc()
      .innerRadius(radius * 0.3)
      .outerRadius(radius * 0.74);

    // Append lines and labels for each arc.
    svg
      .append("g")
      .attr("text-anchor", "middle")
      .style("fill", "black") // Set the font color here
      .selectAll("allPolylines")
      .data(arcs)
      .join("polyline")
      .attr("stroke", (d) => color(d.data.meterType))
      .style("fill", "none")
      .attr("stroke-width", 4)
      .attr("points", function (d) {
        const posA = arc.centroid(d); // line insertion in the slice
        const posB = outerArc.centroid(d); // line break: we use the other arc generator that has been built only for that
        const posC = outerArc.centroid(d); // Label position = almost the same as posB
        const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2; // we need the angle to see if the X position will be at the extreme right or extreme left
        posC[0] = radius * 0.5 * (midangle < Math.PI ? 1 : -1); // multiply by 1 or -1 to put it on the right or on the left
        return [posA, posB, posC];
      });

    // Add the white circle line
    svg
      .append("circle")
      .attr("cx", 0) // X position at center
      .attr("cy", 0) // Y position at center
      .attr("r", (Math.min(width, height) / 2 - margin.top) * 0.75) // Radius slightly less than the outer radius of the pie chart
      .attr("fill", "none")
      .attr("stroke", "white")
      .attr("stroke-width", 12);

    // Add labels
    svg
      .append("g")
      .attr("text-anchor", "middle")
      .style("font-family", "National2")
      .style("font-weight", "500")
      .style("fill", "black")
      .selectAll("allLabels")
      .data(arcs)
      .join("text")
      .text((d) => capitalizeFirstLetter(d.data.meterType))
      .attr("transform", (d) => {
        const pos = outerArc.centroid(d);
        const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        pos[0] = radius * 0.55 * (midangle < Math.PI ? 1 : -1);
        return `translate(${pos})`;
      })
      .style("text-anchor", (d) => {
        const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        return midangle < Math.PI ? "start" : "end";
      });

    // Add labels with current value
    svg
      .append("g")
      .attr("text-anchor", "middle")
      .style("fill", "black")
      .selectAll("allLabels")
      .data(arcs)
      .join("text")
      .text((d) => ` ${Math.round(d.data.curValue)} kWh`)
      .attr("transform", (d) => {
        const pos = outerArc.centroid(d);
        const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        pos[0] = radius * 0.55 * (midangle < Math.PI ? 1 : -1);
        pos[1] += 30;
        return `translate(${pos})`;
      })
      .style("text-anchor", (d) => {
        const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        return midangle < Math.PI ? "start" : "end";
      })
      .style("font-family", "National2")
      .style("font-size", "21px");
    // Append text for last updated timestamp
    // svg
    //   .append("text")
    //   .attr("class", "last-updated")
    //   // .attr("x", 520) // Position the text in the bottom right corner
    //   // .attr("y", 290)
    //   .attr("x", width - 50)
    //   .attr("y", height + 50)

    //   .attr("text-anchor", "end")
    //   .attr("font-size", "14px")
    //   .style("font-style", "italic")
    //   .attr("font-family", "National2")
    //   .text("Last Updated: " + formatTime(lastUpdated));
  }, [graphData, height, width]);

  return graphData.length === 0 ? (
    <Commet color="#32cd32" size="medium" text="" textColor="" />
  ) : (
    <svg width={width} height={height} id="piechart" ref={ref} />
  );
};
export default PieChart;
